import * as React from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import styled from '@emotion/styled';
import { useModal } from 'mui-modal-provider';

import LoadingButton from '../../../../components/Button/LoadingButton';
import ManageInAppDialog from '../../../../components/Dialogs/ManageInApp/ManageInAppDialog';
import { OfferCategory } from '../../../../graphql/API';
import { StripePriceWithMetadata } from '../../../../redux/features/subscription/types';
import { State as SubscriptionState } from '../../context/types';
import { BrandPlan, LogicCondition, Plan, brandPlans, plans } from '../../types';
import Benefit from './Benefit';
import PlanPrice from './PlanPrice';
import { brandDataNew, brandDataOld, brandMapCondition, getData, mapCondition } from './data';

export type PlanGlow = 'starter' | 'plus';

const glowPalette: Record<PlanGlow, { spin: string; stroke: string }> = {
  starter: {
    spin:
      'conic-gradient(from 0deg, transparent 0%, transparent 48%, #9BE86B 66%, #2FA600 84%, transparent 100%)',
    stroke: '#429C12',
  },
  plus: {
    spin:
      'conic-gradient(from 0deg, transparent 0%, transparent 48%, #E0A3FF 66%, #FF6FD8 84%, transparent 100%)',
    stroke: '#D86EEF',
  },
};

const subscribeButtonPalette: Record<
  'starter' | 'plus',
  { background: string; hover: string; ring: string }
> = {
  starter: {
    background: '#429C12',
    hover: '#357C0E',
    ring: 'rgba(66, 156, 18, 0.16)',
  },
  plus: {
    background: '#b084e7',
    hover: '#9870d3',
    ring: 'rgba(176, 132, 231, 0.16)',
  },
};

export type PlanContentProps = {
  highlight: boolean;
  value: string;
  disabled: boolean;
  price: StripePriceWithMetadata | undefined;
  currentSubscription?: string;
  disableButton?: boolean;
  subscriptionState: SubscriptionState;
  onSubscriptionClick: (params: { value: Plan; logicCondition: LogicCondition }) => void;
  isBrand?: boolean;
  venueType?: OfferCategory | null;
  isExistingOldSubscriber?: boolean;
  glow?: PlanGlow;
};

const PlanContent: React.FC<PlanContentProps> = ({
  onSubscriptionClick,
  price,
  subscriptionState,
  disableButton,
  highlight,
  value,
  disabled,
  isBrand = false,
  venueType,
  isExistingOldSubscriber = false,
  glow,
}) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const { showModal } = useModal();

  const style = React.useMemo(() => {
    if (highlight && !disabled) {
      return {
        border: '2px solid rgba(73, 178, 16, 0.50)',
        borderColor: 'rgba(73, 178, 16, 0.50)',
        boxShadow: '0px 0px 20px 0px rgba(17, 61, 13, 0.08)',
      };
    }
    if (highlight && disabled) {
      return { border: 'none' };
    }
    return undefined;
  }, [disabled, highlight]);

  const logicCondition = React.useMemo(
    () =>
      !isBrand
        ? mapCondition[subscriptionState.logicPlan][plans.indexOf(value as Plan)]
        : brandMapCondition[subscriptionState.logicPlan][brandPlans.indexOf(value as BrandPlan)],
    [value, subscriptionState.logicPlan, isBrand],
  );

  const d = React.useMemo(
    () =>
      isBrand
        ? (isExistingOldSubscriber ? brandDataOld : brandDataNew)[value as BrandPlan]
        : getData(venueType)[value as Plan],
    [value, isBrand, venueType, isExistingOldSubscriber],
  );

  const onClickHandler = React.useCallback(() => {
    if (!subscriptionState.isWebPlatform) {
      showModal(ManageInAppDialog, {});
      return;
    }
    const loading = onSubscriptionClick({ value: value as Plan, logicCondition });
    setIsLoading(Boolean(loading));
  }, [value, onSubscriptionClick, logicCondition, subscriptionState.isWebPlatform, showModal]);

  const card = (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        flexDirection: 'column',
        boxShadow: '0px 0px 20px 0px rgba(0, 0, 0, 0.06)',
        border: '1px solid #E2E2E2',
        borderRadius: '12px',
        px: 3,
        py: 4,
        minHeight: '500px',
        backgroundColor: '#ffffff',
        position: 'relative',
        zIndex: 1,
        ...style,
        ...(glow
          ? {
              border: `1.5px solid ${glowPalette[glow].stroke}`,
              borderColor: glowPalette[glow].stroke,
            }
          : {}),
      }}
    >
      <Typography
        component='div'
        variant='subtitle1'
        fontWeight={500}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '26px',
          minHeight: '42px',
        }}
      >
        {d.title}
        <span>{d.highlight}</span>
      </Typography>

      <Box sx={{ marginTop: '4px', marginBottom: '4px' }}>
        <PlanPrice price={price} />
      </Box>

      <Typography
        component='div'
        variant='subtitle1'
        sx={{
          marginTop: 2,
          marginBottom: '16px',
          display: 'flex',
          fontSize: '18px',
          fontWeight: '500',
        }}
      >
        {d.benefitTitle}
      </Typography>

      <Stack spacing={2}>
        {d.benefits.map((x, i) => (
          <Benefit key={i} color={d.benefitColor} {...x} />
        ))}
      </Stack>

      <Typography variant='body2' sx={{ marginTop: 3 }}>
        {d.tips}
      </Typography>

      <Box
        sx={{
          marginTop: 'auto',
          pt: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <LoadingButton
          disabled={disableButton}
          size='large'
          variant={logicCondition.selected ? 'text' : 'outlined'}
          loading={isLoading}
          onClick={onClickHandler}
          fullWidth
          sx={{
            paddingY: '10px',
            borderRadius: '8px',
            fontWeight: 500,
            fontSize: '15px',
            margin: '16px 0',
            whiteSpace: 'nowrap',
            width: '100%',
            color: logicCondition.selected ? '#707070' : '',
            border: !logicCondition.selected ? '1.5px solid #111' : '',
            ...(!logicCondition.selected &&
            logicCondition.kind === 'plan-upgrade' &&
            (value === 'starter' || value === 'plus')
              ? {
                  background: subscribeButtonPalette[value as 'starter' | 'plus'].background,
                  color: '#ffffff',
                  border: 'none',
                  boxShadow: `0 0 0 4px ${subscribeButtonPalette[value as 'starter' | 'plus'].ring}`,
                  '&:hover': {
                    background: subscribeButtonPalette[value as 'starter' | 'plus'].hover,
                    border: 'none',
                    boxShadow: `0 0 0 4px ${subscribeButtonPalette[value as 'starter' | 'plus'].ring}`,
                  },
                  '&.Mui-disabled': {
                    background: subscribeButtonPalette[value as 'starter' | 'plus'].background,
                    color: '#ffffff',
                    opacity: 0.6,
                  },
                }
              : {}),
          }}
        >
          {logicCondition.label}
        </LoadingButton>
        {isBrand && (subscriptionState.plan !== value || !subscriptionState.currentSubscription) ? (
          <CancelAnytime>Cancel anytime</CancelAnytime>
        ) : (
          <CancelAnytime>{value !== 'basic' ? 'Cancel anytime' : ''}</CancelAnytime>
        )}
      </Box>
    </Box>
  );

  if (!glow) return card;

  return (
    <Box sx={{ position: 'relative', height: '100%', borderRadius: '12px' }}>
      {/* Animated colored glow surrounding the upgrade target card */}
      <Box
        sx={{
          position: 'absolute',
          inset: '-3px',
          zIndex: 0,
          borderRadius: '15px',
          overflow: 'hidden',
          pointerEvents: 'none',
          filter: 'blur(4px)',
          opacity: 0.9,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: 900,
            height: 900,
            transform: 'translate(-50%, -50%)',
            background: glowPalette[glow].spin,
            animation: 'planCardGlowSpin 6s linear infinite',
            '@keyframes planCardGlowSpin': {
              to: { transform: 'translate(-50%, -50%) rotate(360deg)' },
            },
          }}
        />
      </Box>
      {card}
    </Box>
  );
};

export default PlanContent;

const CancelAnytime = styled.span`
  display: inline-block;
  color: #5f5f60;
  font-size: 15px;
  font-weight: 400;
  font-style: italic;
  min-height: 24px;
`;
